import React from "react";
import PropTypes from "prop-types";
import { Pencil } from "lucide-react";
import { useSelector } from "react-redux";

import messages from "../../../../assets/locale/messages";
import TruncatedWithTooltip from "../../../TruncatedWithTooltip";
import UIButton from "../../../ui/UIButton";
import UIPageHeader from "../../../ui/UIPageHeader";
import { formatEventStatusTabLabel } from "../../../../utils/Helpers";

import "./EventHeader.scss";

const EventHeader = ({
  event,
  editLabel,
  onEdit,
  showEditButton,
  editDisabled,
  beforeEditAction,
  editSize,
  statusKey,
}) => {
  const lang = useSelector((state) => state.locale.lang);
  const { events } = messages[lang];
  const t = events.viewEvents;
  const eventName =
    typeof event?.name === "string"
      ? event.name
      : lang === "ar"
      ? event?.name_ar || event?.name_en || ""
      : event?.name_en || event?.name_ar || "";
  const status = statusKey ?? event?.status;
  const statusLabel = formatEventStatusTabLabel(
    status,
    events.listEvents.eventTabs,
  );
  // PROLINE-3866/3867: `saudi_applicants_count` is always present (0 when the
  // event has no approved applicants yet); `junior_applicants_percentage` is
  // `null` before the event launches, so that figure is left out until then.
  const saudiApplicantsLabel =
    event?.saudi_applicants_count != null
      ? t.saudiApplicantsCount.replace("{count}", event.saudi_applicants_count)
      : null;
  const juniorApplicantsLabel =
    event?.junior_applicants_percentage != null ? (
      <>
        <span>{event.junior_applicants_percentage}</span>
        <span>%</span> {t.juniorApplicantsSuffix}
      </>
    ) : null;
  const actions =
    beforeEditAction || showEditButton ? (
      <>
        {beforeEditAction}
        {showEditButton ? (
          <UIButton
            type="button"
            appearance="gradient"
            size={editSize}
            startIcon={<Pencil size={16} />}
            onClick={onEdit}
            disabled={editDisabled}
          >
            {editLabel}
          </UIButton>
        ) : null}
      </>
    ) : null;

  return (
    <UIPageHeader
      actions={actions}
      avatar={{
        ...(event?.image_url ? { image: event.image_url } : {}),
        name: eventName,
        size: "lg",
      }}
      className="event-visual-header"
      status={status}
      statusLabel={statusLabel}
      subtitle={[
        <TruncatedWithTooltip
          key="description"
          title={event?.description}
          lines={2}
          className="event-visual-header__meta-desc-wrap"
        >
          <span>{event?.description}</span>
        </TruncatedWithTooltip>,
        <span key="event-id">
          {t.eventId}{" "}
          <span className="event-visual-header__id"> #{event?.id} </span>
        </span>,
        saudiApplicantsLabel,
        juniorApplicantsLabel,
      ]}
      title={
        <TruncatedWithTooltip title={eventName || "—"} lines={2}>
          <span>{eventName || "—"}</span>
        </TruncatedWithTooltip>
      }
    />
  );
};

EventHeader.propTypes = {
  /** Status shown in the chip when it differs from `event.status` (e.g. company Draft). */
  statusKey: PropTypes.string,
  event: PropTypes.shape({
    name: PropTypes.string,
    name_en: PropTypes.string,
    name_ar: PropTypes.string,
    status: PropTypes.string,
    description: PropTypes.string,
    image_url: PropTypes.string,
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    saudi_applicants_count: PropTypes.number,
    junior_applicants_percentage: PropTypes.number,
  }),
  editLabel: PropTypes.string.isRequired,
  onEdit: PropTypes.func,
  showEditButton: PropTypes.bool,
  editDisabled: PropTypes.bool,
  beforeEditAction: PropTypes.node,
  editSize: PropTypes.oneOf(["small", "medium", "large"]),
};

EventHeader.defaultProps = {
  event: {},
  onEdit: () => {},
  showEditButton: true,
  editDisabled: false,
  beforeEditAction: null,
  editSize: "large",
  statusKey: undefined,
};

export default EventHeader;
